import { Search, FileDownload } from '@mui/icons-material';
import { Chip, InputAdornment, MenuItem, OutlinedInput, Select, TextField, CircularProgress } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { useSelector } from 'react-redux';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import DataSheet from '@/components/Modal/Project/DataSheet';
import Delete from '@/components/Modal/General/Delete';
import Loader from '@/components/ui/Loader/Loader';

import project from '@/api/project';
import usePatch from '../../hooks/usePatch';
import { route } from '@/constants/routeName';

const { getProducts, getProductGroup, getProductCategory } = project();

const options = [
	{ id: 'standard', name: 'Standard', value: 0 },
	{ id: 'non-standard', name: 'Non-standard', value: 1 },
	{ id: 'arkiv', name: 'Arkiv', value: 2 },
];

export default function Projects() {
	const navigate = useNavigate();
	const projectStatus = useSelector((state) => state.content.projectStatusD);
	const productPrefix = useSelector((state) => state.content.productPrefix);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [filter, setFilter] = useState({
		search: '',
		// status: '',
		type: [],
		statuses: [],
		group: '',
		category: '',
		productprefixid: '',
	});
	const [search] = useDebounce(filter.search, 500);
	const [isDeleteProduct, setIsDeleteProduct] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const oC = useRef(null);
	const selectGroupRef = useRef(null);
	const selectCategoryRef = useRef(null);
	const selectCategoryKode = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize, order_by: '-productid', deleted_component: true, latest_component: true };
		if (search) {
			obj.search = search;
			// obj.max_size = true;
			// delete obj.page;
		}
		if (filter.group) {
			obj.group = filter.group;
		}
		if (filter.category) {
			obj.category = filter.category;
		}
		if (filter.productprefixid) {
			obj.productprefixid = filter.productprefixid;
		}
		if (filter.statuses.length > 0) {
			obj.status = filter.statuses.map((p) => p);
		}
		if (filter.type.length > 0) {
			obj.product_type = filter.type.map((p) => p);
		}
		return obj;
	}, [filter, search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['projects-file', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProducts(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length;
			if (isFetched && count) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]) || [];

	const handleChangeFilter = (e, key) => {
		const val = e;
		setFilter((prevState) => {
			return { ...prevState, [key]: val };
		});
	};

	const columns = [
		{
			field: 'productid',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			width: 56,
			renderCell: (index) => {
				const pM = paginationModel;
				const idx = index.api.getAllRowIds().indexOf(index.id) + 1;
				return idx + pM.page * pM.pageSize;
			},
		},
		{
			field: 'productprefix',
			headerName: 'Kode',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productcodenumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'productversion',
			headerName: 'Versjon',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'status',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { row } = params;

				return (
					<button
						title='Slette product'
						aria-label='Slette product'
						onClick={() =>
							setIsDeleteProduct({
								id: row.productid,
								name: `${row.productprefix} ${row.productcodenumber} ${row.productname} versjon ${row.productversion}`,
							})
						}
					>
						<Trash2 size={16} />
					</button>
				);
			},
		},
	];

	const { data: g } = useQuery({
		queryKey: ['project-group'],
		queryFn: () => getProductGroup({ max_size: true, order_by: 'groupname', status: 1 }),
	});

	const { data: c } = useQuery({
		queryKey: ['project-category'],
		queryFn: () => getProductCategory({ max_size: true, status: 1, order_by: 'categoryname' }),
	});

	const groups = useMemo(() => g?.data?.results || g?.data || [], [g]);
	const categories = useMemo(() => c?.data || null, [c]);

	const onSaveDataSheet = () => {};

	const usedCategories = useMemo(() => {
		if (filter.group) {
			return categories.filter((c) => c.group_id === filter.group);
		}
		return categories;
	}, [categories, filter.group]);

	const {
		mutate: deleteCategory,
		isPending,
		isSuccess,
	} = usePatch({
		url: `delete_component/${isDeleteProduct?.id}`,
		method: 'post',
		callback: () => {
			toast.success(`product deleted`);
			refetch();
			setIsDeleteProduct(false);
		},
		// onError: () => {},
		showError: true,
	});

	const handleDelete = () => {
		if (!isDeleteProduct) return;
		deleteCategory();
	};

	const exportToExcel = async () => {
		setIsExporting(true);
		try {
			// Create export parameters based on current filters
			const exportParams = {
				...objFilters,
				max_size: true,
				order_by: '-productid',
				deleted_component: true,
				latest_component: true
			};

			// Remove pagination parameters for export
			delete exportParams.page_size;
			delete exportParams.page;

			// Add search parameter if it exists
			if (search) {
				exportParams.search = search;
			}

			// Fetch all data with current filters
			const exportResponse = await getProducts(exportParams);
			const exportDataList = exportResponse?.data?.results || exportResponse?.data || [];

			// Helper function to format date and time
			const formatDateTime = (dateTimeString) => {
				if (!dateTimeString) return '';
				try {
					const date = new Date(dateTimeString);
					const dateStr = date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
					const timeStr = date.toLocaleTimeString('en-GB', {
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit'
					}); // HH:MM:SS format
					return `${dateStr} ${timeStr}`;
				} catch (error) {
					return dateTimeString; // Return original if parsing fails
				}
			};

			// Prepare data for export with all requested fields
			const exportData = exportDataList.map(item => ({
				'Product ID': item.productid,
				'Product Prefix': item.productprefix,
				'Product Code Number': item.productcodenumber,
				'Product Name': item.productname,
				'Product Version': item.productversion,
				'Description': item.description,
				'Product Type': item.producttype_name,
				'Status': item.status_label,
				'Product URL': item.producturl,
				'Datasheet Link': item.datasheetlink,
				'Create Date': formatDateTime(item.createdate),
				'Created By Email': item.createdby_email,
				'Update By': item.updateby_email,
				'Update Timestamp': formatDateTime(item.updatetimestamp),
			}));

			// Create a new workbook
			const wb = XLSX.utils.book_new();

			// Create a worksheet from the data
			const ws = XLSX.utils.json_to_sheet(exportData);

			// Add the worksheet to the workbook
			XLSX.utils.book_append_sheet(wb, ws, 'Projects');

			// Generate filename with current date
			const currentDate = new Date().toISOString().split('T')[0];
			const filename = `projects_export_${currentDate}.xlsx`;

			// Write and download the file
			XLSX.writeFile(wb, filename);

			toast.success(`Excel file exported successfully! ${exportData.length} records exported.`);
		} catch (error) {
			toast.error('Failed to export Excel file');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			<BDHelmet title='Datasheet' description='BDS Samferdsel project datasheet' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<div className='flex flex-wrap items-center gap-4'>
							<TextField
								className='w-48 h-12 input-search'
								placeholder='Search'
								onChange={(e) => {
									setPaginationModel((prevState) => {
										return { ...prevState, page: 0 };
									});
									handleChangeFilter(e.target.value, 'search');
									if (e.target.value && paginationModel.page > 0) {
										setPaginationModel((prevState) => {
											return { ...prevState, page: 0 };
										});
									}
								}}
								size='small'
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											<Search style={{ color: 'black' }} />
										</InputAdornment>
									),
								}}
							/>


							<div className='flex items-center gap-1'>
								<span>Group</span>

								<Select
									ref={selectGroupRef}
									displayEmpty
									labelId='group'
									id='group'
									className='min-w-48 select-default '
									value={filter.group}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'group');
										if (val && val !== filter.group && filter.category) {
											handleChangeFilter('', 'category');
										}
										selectGroupRef.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg group--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg group--
										</em>
									</MenuItem>
									{groups?.map((p) => (
										<MenuItem key={p.id} value={p.id} name={p.groupname}>
											{p.groupname}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Category</span>

								<Select
									ref={selectCategoryRef}
									displayEmpty
									labelId='category'
									id='category'
									className='min-w-48 select-default '
									value={filter.category}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'category');
										selectCategoryRef.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg category--' />}
									placeholder='--Velg category--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg category--
										</em>
									</MenuItem>
									{usedCategories?.map((p) => (
										<MenuItem key={p.id} value={p.id} name={p.categoryname}>
											{p.categoryname}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Kode</span>

								<Select
									ref={selectCategoryKode}
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.productprefixid}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'productprefixid');
										selectCategoryKode.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg kode--
										</em>
									</MenuItem>
									{productPrefix?.map((p) => (
										<MenuItem key={p.productprefixid} value={p.productprefixid} name={p.productprefix}>
											{p.productprefix}
										</MenuItem>
									))}
								</Select>
							</div>

							<div className='flex items-center gap-1'>
								<span>Status</span>

								<Select
									displayEmpty
									labelId='status'
									id='status'
									className='py-0 min-w-48 select-default'
									value={filter.statuses}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'statuses');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg status--' className='py-0' />}
									placeholder='--Velg status--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = projectStatus.find((opt) => opt.id_status === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.label} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300'>--Velg status--</em>
									</MenuItem>
									{projectStatus?.map((p) => (
										<MenuItem key={p.id} value={p.id_status} name={p.label}>
											{p.label}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Type</span>

								<Select
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.type}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'type');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = options.find((opt) => opt.value === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.name} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg status--
										</em>
									</MenuItem>
									{options?.map((p) => (
										<MenuItem key={p.id} value={p.value} name={p.name}>
											{p.name}
										</MenuItem>
									))}
								</Select>
							</div>

							<button
								onClick={exportToExcel}
								disabled={!dataList.length || isExporting}
								title='Export to Excel'
								className='relative flex items-center gap-2 min-w-[124px] h-10 btn'
							>
								<div className='flex items-center justify-center w-5 h-5 rounded-full'>
									{isExporting ? <CircularProgress size={16} color='inherit' /> : <FileDownload />}
								</div>
								{isExporting ? 'Exporting...' : 'Export to Excel'}
							</button>
						</div>
					</div>
					<div>
						<span className='font-bold'>Vedlegg </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>total</span>
					</div>
					<div className='w-full mx-auto'>
						{(isPending && !isSuccess) || isExporting ? <Loader /> : null}

						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							className='mx-auto lg:max-w-[1920px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							getRowId={(row) => row.productid}
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.productid === params.row.productid) {
									cellClass += ' font-bold border border-black';
								}
								if (+params.row.status === 0) {
									cellClass += ' !bg-bd-gLight/50';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								navigate(`/${route.projects}/detail/${params.row.productcodenumber}`);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{!!selectedRow && (
					<DataSheet open={!!selectedRow} onSave={onSaveDataSheet} data={selectedRow} onClose={() => setSelectedRow(false)} />
				)}
				{!!isDeleteProduct && (
					<Delete open={!!isDeleteProduct} data={isDeleteProduct} onSave={handleDelete} onClose={() => setIsDeleteProduct(false)} />
				)}
			</div>
		</>
	);
}
